import { useEffect, useRef } from "react";
import Lenis from "lenis";

declare global {
  interface Window {
    __lenis?: Lenis | null;
  }
}

/**
 * Butter-smooth scroll wrapper for the whole app.
 * Only initializes on the client; SSR sees plain native scrolling.
 */
export function SmoothScroll({ children }: { children: React.ReactNode }) {
  const lenisRef = useRef<Lenis | null>(null);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
      wheelMultiplier: 1,
      touchMultiplier: 2,
    });

    lenisRef.current = lenis;
    if (typeof window !== "undefined") window.__lenis = lenis;

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Sync Lenis to the browser's restored scroll on back/forward nav.
    // Without this the internal target stays at 0 and the next wheel
    // event snaps the page, which feels like scrolling is blocked.
    const syncToWindow = () => {
      try {
        lenis.resize();
        lenis.scrollTo(window.scrollY, { immediate: true, force: true });
      } catch {}
    };
    const onPopState = () => {
      // Run after the browser applies its scroll restoration.
      window.setTimeout(syncToWindow, 0);
      window.setTimeout(syncToWindow, 200);
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) onPopState();
    };
    window.addEventListener("popstate", onPopState);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      window.removeEventListener("popstate", onPopState);
      window.removeEventListener("pageshow", onPageShow);
      lenis.destroy();
      lenisRef.current = null;
      if (typeof window !== "undefined") window.__lenis = null;
    };
  }, []);

  return <>{children}</>;
}
